import { useEffect, useMemo, useState } from "react";
import { api, formatError, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Modal } from "../components/ui-kit";
import { ArrowRightLeft, Check, X, Plus, CheckCheck, XCircle } from "lucide-react";
import QRScannerButton from "../components/QRScannerButton";

export default function TransfersPage() {
  const { user } = useAuth();
  const isTech = user?.role === "tecnico";
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [stock, setStock] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ item_type: "material", to_username: "", material_id: "", quantity: 1, equipment_unit_id: "", note: "" });
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [processing, setProcessing] = useState(false);

  const load = () => {
    api.get("/transfer-requests?direction=incoming").then((r) => setIncoming(r.data));
    api.get("/transfer-requests?direction=outgoing").then((r) => setOutgoing(r.data));
    // Only technicians own personal stock to transfer FROM.
    if (isTech) {
      api.get("/technician-stock").then((r) => setStock(r.data)).catch(() => setStock([]));
      api.get("/equipment-units?status=com_tecnico").then((r) => setUnits(r.data)).catch(() => setUnits([]));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isTech]);

  const openNew = () => { setErr(""); setForm({ item_type: "material", to_username: "", material_id: "", quantity: 1, equipment_unit_id: "", note: "" }); setOpen(true); };

  const send = async () => {
    setErr("");
    try {
      await api.post("/transfer-requests", form);
      setOpen(false); load();
    } catch (e) { setErr(formatError(e)); }
  };

  const accept = async (id) => {
    try { await api.post(`/transfer-requests/${id}/accept`); load(); }
    catch (e) { alert(formatError(e)); }
  };
  const reject = async (id) => {
    if (!window.confirm("Rejeitar esta transferência?")) return;
    try { await api.post(`/transfer-requests/${id}/reject`); load(); }
    catch (e) { alert(formatError(e)); }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const allSelected = useMemo(
    () => incoming.length > 0 && incoming.every((r) => selected.has(r.id)),
    [incoming, selected]
  );
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(incoming.map((r) => r.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkAccept = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Aceitar ${selected.size} transferência(s)?`)) return;
    setProcessing(true);
    try {
      await api.post("/transfer-requests/bulk-accept", { ids: Array.from(selected) });
      clearSelection(); load();
    } catch (e) { alert(formatError(e)); }
    finally { setProcessing(false); }
  };
  const bulkReject = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Rejeitar ${selected.size} transferência(s)?`)) return;
    setProcessing(true);
    try {
      await api.post("/transfer-requests/bulk-reject", { ids: Array.from(selected) });
      clearSelection(); load();
    } catch (e) { alert(formatError(e)); }
    finally { setProcessing(false); }
  };

  const badge = (s) => {
    const map = { pending: ["Pendente", "badge-progress"], accepted: ["Aceita", "badge-done"], rejected: ["Rejeitada", "badge-cancel"] };
    const [lbl, cls] = map[s] || [s, "badge-open"];
    return <span className={`badge ${cls}`}>{lbl}</span>;
  };

  return (
    <div data-testid="transfers-page">
      <PageHeader
        eyebrow="Estoque"
        title="Traspasso de Materiais"
        actions={isTech ? <button className="btn btn-primary" onClick={openNew} data-testid="new-transfer-btn"><Plus size={14} /> Novo Traspasso</button> : null}
      >
        {isTech
          ? "Envie material ou equipamento para outro técnico. O destinatário precisa aceitar para entrar no estoque dele."
          : "Acompanhe os traspassos entre técnicos. Aprovações em lote e histórico geral."}
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="px-5 py-4 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--border)" }}>
            <h3 className="label-caps">Recebidas · Pendentes</h3>
            {selected.size > 0 && (
              <div className="flex items-center gap-2 text-xs" data-testid="bulk-toolbar">
                <span style={{ color: "var(--text-dim)" }}>{selected.size} selecionada(s)</span>
                <button className="btn btn-primary" onClick={bulkAccept} disabled={processing} data-testid="bulk-accept">
                  <CheckCheck size={13} /> Aceitar
                </button>
                <button className="btn btn-danger" onClick={bulkReject} disabled={processing} data-testid="bulk-reject">
                  <XCircle size={13} /> Rejeitar
                </button>
                <button className="btn btn-ghost" onClick={clearSelection} data-testid="bulk-clear">Limpar</button>
              </div>
            )}
          </div>
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  {incoming.length > 0 && (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      data-testid="bulk-select-all"
                      aria-label="Selecionar todas"
                    />
                  )}
                </th>
                <th>De</th><th>Item</th><th>Data</th><th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {incoming.map((r) => (
                <tr key={r.id} data-testid={`incoming-${r.id}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleOne(r.id)}
                      data-testid={`bulk-check-${r.id}`}
                      aria-label={`Selecionar ${r.id}`}
                    />
                  </td>
                  <td>{r.from_user_name}</td>
                  <td className="text-xs">
                    {r.item_type === "material"
                      ? <>{r.quantity}x {r.material_name} <span className="mono" style={{ color: "var(--text-mute)" }}>({r.material_code})</span></>
                      : <>{r.equipment_model} <span className="mono">SN {r.equipment_serial}</span></>}
                    {r.note && <div style={{ color: "var(--text-mute)" }}>{r.note}</div>}
                  </td>
                  <td className="text-xs">{formatDate(r.created_at)}</td>
                  <td>
                    <div className="flex gap-1">
                      <button className="btn btn-primary" onClick={() => accept(r.id)} data-testid={`accept-${r.id}`}><Check size={13} /> Aceitar</button>
                      <button className="btn btn-danger" onClick={() => reject(r.id)} data-testid={`reject-${r.id}`}><X size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {incoming.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-sm" style={{ color: "var(--text-mute)" }}>Nada pendente para você.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}><h3 className="label-caps">Enviadas · Histórico</h3></div>
          <table className="data">
            <thead><tr><th>Para</th><th>Item</th><th>Status</th></tr></thead>
            <tbody>
              {outgoing.map((r) => (
                <tr key={r.id}>
                  <td>{r.to_user_name}</td>
                  <td className="text-xs">{r.item_type === "material" ? `${r.quantity}x ${r.material_name}` : `${r.equipment_model} SN ${r.equipment_serial}`}</td>
                  <td>{badge(r.status)}</td>
                </tr>
              ))}
              {outgoing.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-sm" style={{ color: "var(--text-mute)" }}>Nenhuma envio ainda.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Novo Traspasso" testid="transfer-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={send} data-testid="send-transfer">Enviar</button>
        </>}>
        {err && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{err}</div>}
        <div className="grid gap-3">
          <div>
            <label className="label-caps block mb-1">Tipo</label>
            <div className="flex gap-2">
              <button type="button" className={`btn ${form.item_type === "material" ? "btn-primary" : "btn-secondary"}`} onClick={() => setForm({ ...form, item_type: "material" })} data-testid="type-material">Material</button>
              <button type="button" className={`btn ${form.item_type === "equipment" ? "btn-primary" : "btn-secondary"}`} onClick={() => setForm({ ...form, item_type: "equipment" })} data-testid="type-equipment">Equipamento (SN)</button>
            </div>
          </div>
          <div>
            <label className="label-caps block mb-1">Para (email ou usuário do técnico)</label>
            <input className="input mono" value={form.to_username} onChange={(e) => setForm({ ...form, to_username: e.target.value })} placeholder="ex: joao ou joao@empresa.com" data-testid="transfer-to-username" />
          </div>
          {form.item_type === "material" ? (
            <>
              <div>
                <label className="label-caps block mb-1">Material do seu estoque</label>
                <select className="select" value={form.material_id} onChange={(e) => setForm({ ...form, material_id: e.target.value })} data-testid="transfer-material-select">
                  <option value="">Selecione</option>
                  {stock.map((s) => <option key={s.material_id} value={s.material_id}>{s.material_code} — {s.material_name} (disp: {s.quantity})</option>)}
                </select>
              </div>
              <div>
                <label className="label-caps block mb-1">Quantidade</label>
                <input className="input mono" type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} data-testid="transfer-quantity" />
              </div>
            </>
          ) : (
            <div>
              <label className="label-caps block mb-1">Equipamento (SN)</label>
              <div className="flex gap-2">
                <select className="select flex-1" value={form.equipment_unit_id} onChange={(e) => setForm({ ...form, equipment_unit_id: e.target.value })} data-testid="transfer-unit-select">
                  <option value="">Selecione</option>
                  {units.map((u) => <option key={u.id} value={u.id}>{u.model_name} · SN {u.serial_number}</option>)}
                </select>
                <QRScannerButton onScan={(sn) => {
                  const u = units.find((x) => (x.serial_number || "").toLowerCase() === sn.toLowerCase());
                  if (u) setForm({ ...form, equipment_unit_id: u.id });
                  else alert("SN não encontrado no seu estoque");
                }} testid="transfer-unit-qr" />
              </div>
            </div>
          )}
          <div>
            <label className="label-caps block mb-1">Observação (opcional)</label>
            <input className="input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
